import { captureMessage } from "@sentry/react";
import { Statsig } from "statsig-react";

import config from "#config";

type Identity = { userID?: string; storeID?: string };

// The SDK retains its initial callbacks. Read identities when called, rather
// than capturing the store that happened to be active at initialization.
export function createStatsigDiagnostics(getActiveIdentity: () => Identity) {
    const reported = new Set<string>();
    let occurrence = 0;

    return (success: boolean, durationMs?: number, message?: string | null) => {
        // Telemetry must never interrupt initialization or feature evaluation.
        try {
            const active = getActiveIdentity();
            const key = JSON.stringify([active.userID, active.storeID]);
            if (success) {
                reported.delete(key);
                return;
            }
            if (reported.has(key)) return;
            const current = Statsig.getCurrentUser();
            const evaluation = Statsig.getEvaluationDetails();
            // Distinct messages survive Sentry's consecutive-event dedupe;
            // the fixed fingerprint still groups them into the same issue.
            captureMessage(`Statsig failed (occurrence ${++occurrence})`, {
                level: "warning",
                fingerprint: ["statsig", "failed"],
                tags: {
                    "statsig.status": "failed",
                    "statsig.environment":
                        config.STATSIG_ENVIRONMENT ?? "unset",
                    "statsig.reason": evaluation.reason,
                    // Other network failures can produce the same symptoms.
                    "statsig.likely_blocked":
                        navigator.onLine &&
                        /failed to fetch|networkerror|load failed/i.test(
                            message ?? "",
                        ),
                    storeId: active.storeID || "missing",
                    "statsig.store_id":
                        current?.customIDs?.storeID || "missing",
                },
                contexts: {
                    statsig: {
                        activeUserID: active.userID,
                        activeStoreID: active.storeID,
                        sdkCurrentUserID: current?.userID,
                        sdkCurrentStoreID: current?.customIDs?.storeID,
                        durationMs,
                        message: message?.slice(0, 500),
                        sdkEvaluationTime: evaluation.time,
                        online: navigator.onLine,
                    },
                },
            });
            reported.add(key);
        } catch {
            // Diagnostics are best effort and must not change gate behavior.
        }
    };
}
